import { enqueueTeamPresetsSync } from '@/lib/realtime-sync/plugins/team-presets';
import type { TeamPreset, TeamId } from '@/schemas';
import { TeamPresetSchema, TeamId as TeamIdSchema, createTeamId, type UserId } from '@/schemas';
import { db } from './client';

/**
 * Normalize team preset name by trimming whitespace and normalizing internal whitespace
 */
export const normalizeTeamPresetName = (name: string): string => {
  // eslint-disable-next-line unicorn/prefer-string-replace-all -- replaceAll doesn't support regex
  return name.trim().replace(/\s+/g, ' ');
};

/**
 * Get all non-deleted team presets from cache table
 */
export const listTeamPresets = async (): Promise<TeamPreset[]> => {
  const presets = await db.teamPresets.filter((preset) => !preset.deletedAt).toArray();
  return presets.map((preset) => TeamPresetSchema.parse(preset));
};

/**
 * Get single team preset by ID
 */
export const getTeamPreset = async (presetId: TeamId): Promise<TeamPreset | undefined> => {
  const id = TeamIdSchema.parse(presetId);
  const preset = await db.teamPresets.get(id);
  if (!preset) return undefined;
  return TeamPresetSchema.parse(preset);
};

/**
 * Search team presets by name or nickname (case-insensitive substring match)
 */
export const searchTeamPresets = async (query: string): Promise<TeamPreset[]> => {
  if (!query.trim()) {
    return listTeamPresets();
  }

  const normalizedQuery = query.toLowerCase().trim();
  const allPresets = await listTeamPresets();

  return allPresets.filter((preset) => {
    const nameMatch = preset.name?.toLowerCase().includes(normalizedQuery);
    const nicknameMatch = preset.nickname?.toLowerCase().includes(normalizedQuery);
    return nameMatch || nicknameMatch;
  });
};

/**
 * Create a new team preset
 */
export type CreateTeamPresetInput = {
  name?: string;
  nickname?: string;
  avatarImageId?: import('@/schemas/ids').ImageId;
  color?: string;
  playerIds?: TeamPreset['playerIds'];
  userId?: UserId;
};

export const createTeamPreset = async (input: CreateTeamPresetInput): Promise<TeamId> => {
  const presetId = createTeamId();
  const now = Date.now();

  const preset: TeamPreset = {
    id: presetId,
    name: input.name ? normalizeTeamPresetName(input.name) : undefined,
    nickname: input.nickname ? normalizeTeamPresetName(input.nickname) : undefined,
    avatarImageId: input.avatarImageId,
    color: input.color,
    playerIds: input.playerIds,
    userId: input.userId, // Optional: system presets don't have userId
    createdAt: now,
    updatedAt: now,
    deletedAt: undefined,
  };

  await db.teamPresets.add(TeamPresetSchema.parse(preset));
  if (preset.userId) {
    enqueueTeamPresetsSync(preset.userId);
  }
  return presetId;
};

/**
 * Update an existing team preset
 */
export type UpdateTeamPresetInput = {
  name?: string;
  nickname?: string;
  avatarImageId?: import('@/schemas/ids').ImageId;
  color?: string;
  playerIds?: TeamPreset['playerIds'];
};

export const updateTeamPreset = async (
  presetId: TeamId,
  input: UpdateTeamPresetInput,
): Promise<void> => {
  const id = TeamIdSchema.parse(presetId);

  const existing = await db.teamPresets.get(id);
  if (!existing || existing.deletedAt) {
    throw new Error('Team preset not found');
  }

  const now = Date.now();

  let name = existing.name;
  if (input.name !== undefined) {
    name = input.name ? normalizeTeamPresetName(input.name) : undefined;
  }

  let nickname = existing.nickname;
  if (input.nickname !== undefined) {
    nickname = input.nickname ? normalizeTeamPresetName(input.nickname) : undefined;
  }

  const avatarImageId =
    input.avatarImageId === undefined ? existing.avatarImageId : input.avatarImageId;
  const color = input.color === undefined ? existing.color : input.color;
  const playerIds = input.playerIds === undefined ? existing.playerIds : input.playerIds;

  const preset: TeamPreset = {
    ...existing,
    name,
    nickname,
    avatarImageId,
    color,
    playerIds,
    updatedAt: now,
  };

  const parsedPreset = TeamPresetSchema.parse(preset);
  await db.teamPresets.put(parsedPreset);
  if (parsedPreset.userId) {
    enqueueTeamPresetsSync(parsedPreset.userId);
  }
};

/**
 * Delete a team preset (soft delete)
 */
export const deleteTeamPreset = async (presetId: TeamId): Promise<void> => {
  const id = TeamIdSchema.parse(presetId);

  const existing = await db.teamPresets.get(id);
  if (!existing) {
    throw new Error('Team preset not found');
  }

  if (existing.deletedAt) {
    throw new Error('Team preset not found');
  }

  const now = Date.now();
  const preset: TeamPreset = {
    ...existing,
    deletedAt: now,
    updatedAt: now,
  };

  const parsedPreset = TeamPresetSchema.parse(preset);
  await db.teamPresets.put(parsedPreset);
  if (parsedPreset.userId) {
    enqueueTeamPresetsSync(parsedPreset.userId);
  }
};

/**
 * Restore a deleted team preset
 */
export const restoreTeamPreset = async (presetId: TeamId): Promise<void> => {
  const id = TeamIdSchema.parse(presetId);

  const existing = await db.teamPresets.get(id);
  if (!existing || !existing.deletedAt) {
    throw new Error('Team preset not found or not deleted');
  }

  const now = Date.now();
  const preset: TeamPreset = {
    ...existing,
    deletedAt: undefined,
    updatedAt: now,
  };

  const parsedPreset = TeamPresetSchema.parse(preset);
  await db.teamPresets.put(parsedPreset);
  if (parsedPreset.userId) {
    enqueueTeamPresetsSync(parsedPreset.userId);
  }
};
